import { Add01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AppLink } from "@/components/common/app-link";
import { EnvironmentBadge, Tag } from "@/components/common/badges";
import { Icon } from "@/components/common/icon";
import { Page, PageTitle, Panel } from "@/components/common/page";
import { Stat } from "@/components/common/stat";
import { EmptyState, PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { q } from "@/data/queries";
import { formatLatency, formatNumber, formatPercent, relativeTime } from "@/lib/format";
import { projectPath } from "@/lib/nav";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/p/$project/webhooks/")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(q.webhooks()),
      context.queryClient.ensureQueryData(q.deliveries({ limit: 500 })),
    ]),
  pendingComponent: PageSkeleton,
  component: WebhooksPage,
});

function WebhooksPage() {
  const { project } = Route.useParams();
  const { data: webhooks } = useSuspenseQuery(q.webhooks());
  const { data: deliveries } = useSuspenseQuery(q.deliveries({ limit: 500 }));
  const delivered = deliveries.items.filter((d) => d.status === "delivered").length;
  const failed = deliveries.items.filter((d) => d.status === "failed").length;

  return (
    <Page wide>
      <PageTitle
        title="Webhooks"
        description="Every event Aux0 emits, signed and retried. A delivery that exhausts its retries is kept for seven days and can be replayed, so nothing is lost while your endpoint is down."
        actions={
          <Button size="sm" disabled>
            <Icon icon={Add01Icon} className="size-3.5" />
            Add endpoint
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border lg:grid-cols-4">
        <Stat label="Endpoints" value={webhooks.filter((w) => w.enabled).length} />
        <Stat label="Delivered" value={formatNumber(delivered)} />
        <Stat
          label="Failed"
          value={formatNumber(failed)}
          tone={failed > 0 ? "stop" : undefined}
          footnote={
            deliveries.items.length
              ? formatPercent(failed / deliveries.items.length, 1)
              : undefined
          }
        />
        <Stat
          label="Median round trip"
          value={formatLatency(
            Math.round(
              webhooks.reduce((sum, w) => sum + w.health.p50Ms, 0) /
                Math.max(webhooks.length, 1),
            ),
          )}
        />
      </div>

      <Panel title="Endpoints" bodyClassName="divide-y">
        {webhooks.length === 0 ? (
          <EmptyState
            title="No endpoints yet"
            description="Add one and Aux0 posts every event it emits to it, signed, with seven days of replay if it goes down."
          />
        ) : (
          webhooks.map((webhook) => {
            const total = webhook.health.delivered + webhook.health.failed;
            const rate = total ? webhook.health.failed / total : 0;
            return (
              <AppLink
                key={webhook.id}
                to={projectPath(project, `/webhooks/${webhook.id}`)}
                className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                <span
                  className={cn(
                    "size-2 shrink-0 rounded-full",
                    !webhook.enabled
                      ? "bg-ink-faint"
                      : webhook.health.failingSince
                        ? "bg-stop"
                        : "bg-ok",
                  )}
                >
                  <span className="sr-only">
                    {!webhook.enabled
                      ? "Disabled"
                      : webhook.health.failingSince
                        ? "Failing"
                        : "Healthy"}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-mono text-[12.5px]">
                      {webhook.url}
                    </span>
                    <EnvironmentBadge environment={webhook.environment} />
                    {!webhook.enabled ? <Tag mono>disabled</Tag> : null}
                  </span>
                  <span className="mt-0.5 block truncate text-[11.5px] text-muted-foreground">
                    {webhook.events.length} events · {webhook.secretPrefix}••••
                  </span>
                </span>
                <span className="shrink-0 text-right text-[11.5px]">
                  <span
                    className={cn(
                      "block tabular",
                      rate > 0.05 ? "text-stop" : "text-muted-foreground",
                    )}
                  >
                    {formatPercent(rate, 1)} failed
                  </span>
                  {webhook.health.failingSince ? (
                    <span className="block text-ink-faint">
                      since {relativeTime(webhook.health.failingSince)}
                    </span>
                  ) : (
                    <span className="block text-ink-faint">
                      p50 {formatLatency(webhook.health.p50Ms)}
                    </span>
                  )}
                </span>
                <Icon
                  icon={ArrowRight01Icon}
                  className="size-4 shrink-0 text-muted-foreground"
                />
              </AppLink>
            );
          })
        )}
      </Panel>
    </Page>
  );
}
